/**
 * What the concept sidebar's body shows instead of a concept —
 * ROUTE_INVENTORY §2.10 #36.
 *
 * Extracted from `ConceptDetailSidebar` for the same reason `GraphStates` was
 * extracted from `KnowledgeArchivePage`: one of these three is unreachable
 * through the product, and the only way to put a baseline on it is to render
 * it directly.
 *
 * ## `ConceptDetailError` cannot be reached through the page
 *
 * `useConceptDetail` **catches** a failed request and falls back to mock data
 * (§2.10 #35), exactly as `useKnowledgeArchiveGraph` does for the graph. A
 * `fail` handler on `GET /mindspace/concepts/:id` therefore renders a
 * plausible concept, not an error — so `error` is never populated in any data
 * mode, and a story written to assert this branch through the sidebar would be
 * asserting the fallback instead. Storied here as plain props, with the page's
 * own file recording the omission.
 *
 * ## Empty is not an error
 *
 * `ConceptDetailEmpty` is what renders when the fetch resolved to nothing —
 * `empty` data mode, or an id the mock resolver cannot synthesize. It reads as
 * a neutral statement rather than a failure, which is right: nothing went
 * wrong, there is simply nothing selected.
 */

import { Loader2 } from 'lucide-react';

/** §2.10 #36 — the detail fetch is in flight. */
export function ConceptDetailLoading() {
  return (
    <div className='flex h-64 items-center justify-center'>
      <Loader2
        className='h-6 w-6 animate-spin text-primary'
        role='status'
        aria-label='Loading concept'
      />
    </div>
  );
}

/**
 * §2.10 #36 — the detail fetch failed.
 *
 * Unreachable through the page; see the module docstring.
 */
export function ConceptDetailError() {
  return (
    <div className='px-5 py-10 text-center'>
      {/* The drawer sits on `--card` (matching the filter panel), where
          `text-destructive-emphasis` measures 4.93:1 — the same token and
          surface `GraphStates` uses. It was a raw `text-red-700` while the
          drawer was on `--background`, where the token only reached 4.08:1;
          if the drawer's surface changes again, re-measure this. */}
      <p className='text-sm text-destructive-emphasis'>
        Failed to load concept details
      </p>
    </div>
  );
}

/** §2.10 #36 — resolved, but there is no concept to show. */
export function ConceptDetailEmpty() {
  return (
    <div className='px-5 py-10 text-center text-sm text-muted-foreground'>
      No concept selected
    </div>
  );
}
